import React, { useEffect, useRef, useState } from 'react'
import pa from '../ProductActivityPage.module.css'
import styles from './Pallets.module.css'
import o from '../../components/Overlay.module.css'
import sd from './SdPanel.module.css'
import { CloseButton } from './SdPanel'
import { apiJson } from '../../lib/api'
import { useHelpPref } from '../../lib/preferences'
import { useLayer, useChanged } from '../../components/layers'
import { PendingButton, inertClass, useSlow, Failed } from '../../components/pending'
import { digits, ordersFor, planErrors, prepBlock, srcLabel, suggest, type PlanResponse, type Ref } from './model'
import { withCurrentRef } from './loads'

// Create pallets (V4 createPallets). Opened from a To palletise row (source
// locked, order optional) or from an order's reservation (both locked). It
// plans; it never confirms. The API re-applies every rule shown here.
//
// Not built: the "print identity labels" checkbox. Label Printing has no
// pallet identity label yet, so there is nothing for it to print.

const errText = (e: any) => e?.body?.error || e?.message || String(e)

export function CreatePalletsPanel({
  user,
  sourceId,
  allocationId,
  onClose,
  onCreated,
}: {
  user: any
  sourceId: string
  allocationId?: string
  onClose: () => void
  onCreated: (message: string) => void
}) {
  const [plan, setPlan] = useState<PlanResponse | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [qty, setQty] = useState('')
  const [parts, setParts] = useState<string[]>([''])
  const [orderId, setOrderId] = useState('')
  const [lineId, setLineId] = useState('')
  const [reqLid, setReqLid] = useState('')
  const [loc, setLoc] = useState('')
  const [helpOn, toggleHelp] = useHelpPref(user, 'create_pallets')

  const userRef = useRef(user)
  userRef.current = user
  useEffect(() => {
    const qs = new URLSearchParams({ source_id: sourceId })
    if (allocationId) qs.set('allocation_id', allocationId)
    apiJson<PlanResponse>(userRef.current, `/api/pallets/plan?${qs}`)
      .then((r) => {
        setPlan(r)
        setQty(r.eligible > 0 ? String(r.eligible) : '')
        setParts(suggest(r.eligible, r.capacity))
        if (r.reservation) {
          setOrderId(r.reservation.order_id)
          setLineId(r.reservation.line_id)
          setReqLid(r.reservation.lid_id || r.source.lid?.id || '')
        } else {
          setReqLid(r.source.lid?.id || '')
        }
      })
      .catch((e) => setLoadError(`Failed to load the source group: ${errText(e)}`))
  }, [sourceId, allocationId])

  const fromAllocation = !!plan?.reservation
  const total = parseInt(qty, 10) || 0
  const errors = plan ? planErrors(qty, parts, plan.eligible, plan.capacity, fromAllocation) : []
  const choices = plan ? ordersFor(plan.orders, plan.source) : []
  const chosen = choices.find((c) => c.order.id === orderId)
  const block = plan?.reservation
    ? prepBlock({ reference: plan.reservation.order_reference, approval_status: plan.reservation.approval_status })
    : prepBlock(chosen?.order)
  const canCreate = !!plan && errors.length === 0 && !block && !busy
  // Archived lids are not offered (§7n.13), except the ones this plan
  // inherits: the order line's and the lid at picking, as the API allows.
  const lineLid = plan?.reservation?.line_id === lineId ? plan.reservation.lid : chosen?.line.id === lineId ? chosen.line.lid : null
  const lidChoices = plan ? [lineLid, plan.source.lid].reduce((acc: Ref[], r) => withCurrentRef(acc, r), plan.lids) : []

  const setTotal = (v: string) => {
    const d = digits(v)
    setQty(d)
    if (plan) setParts(suggest(parseInt(d, 10) || 0, plan.capacity))
  }

  const chooseOrder = (id: string) => {
    setOrderId(id)
    const c = choices.find((x) => x.order.id === id)
    setLineId(c ? c.line.id : '')
    if (c) setReqLid(c.line.lid?.id || '')
    else setReqLid(plan?.source.lid?.id || '')
  }

  // A layer (h 425-458): Escape, the scrim, the × and Cancel ask first once a
  // value has moved from the plan as it loaded. h 3984-3992: busy prevents
  // close, Cancel disabled while busy.
  const panelEl = useRef<HTMLDivElement>(null)
  const slow = useSlow(busy)
  const dirty = useChanged({ qty, parts, orderId, lineId, reqLid, loc }, plan !== null)
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: dirty && !busy, el: panelEl, modal: true, focusFirst: plan !== null, busy })

  const create = async () => {
    if (!plan) return
    setBusy(true)
    setSaveError(null)
    try {
      const r = await apiJson<{ message: string }>(userRef.current, '/api/pallets', {
        method: 'POST',
        body: {
          source_id: sourceId,
          allocation_id: allocationId || null,
          order_line_id: lineId || null,
          required_lid_id: reqLid || null,
          location_id: loc || null,
          boxes: total,
          parts: parts.map((x) => parseInt(x, 10) || 0),
        },
      })
      onCreated(r.message)
    } catch (e) {
      setSaveError(errText(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={o.drawerScrim} onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) requestClose() }}>
      <div ref={panelEl} tabIndex={-1} className={`${o.drawer} ${sd.panel}`} role="dialog" aria-label="Create pallets">
        <div className={styles.dHead}>
          <span className={styles.dTitle}>Create pallets</span>
          <div className={styles.dHeadRight}>
            <button type="button" className={pa.helpBtn} onClick={toggleHelp}>
              {helpOn ? 'Hide help' : 'How this works'}
            </button>
            <CloseButton onClick={requestClose} disabled={busy} />
          </div>
        </div>
        <div className={`${styles.dBody} ${inertClass(busy, slow)}`}>
          {loadError && (
            <div className={styles.alert} role="alert">
              {loadError}
            </div>
          )}
          {helpOn && (
            <div className={styles.helpBox}>
              <button type="button" className={styles.helpX} title="Hide help" aria-label="Hide help" onClick={toggleHelp}>
                ×
              </button>
              <div className={styles.helpItem}>
                <span>You are planning, not confirming</span>
                <p>
                  This creates planned pallets and earmarks the boxes so another plan cannot reuse them. Nobody has counted anything yet. A pallet
                  becomes confirmed only when someone confirms what is physically on it.
                </p>
              </div>
              <div className={styles.helpItem}>
                <span>The split must add up</span>
                <p>
                  Step 2 is the total; step 3 is how it is divided. The individual pallets are suggested at capacity with the remainder last, and you
                  can change them, but they must sum to the total and none may exceed capacity. Anything under capacity is marked partial.
                </p>
              </div>
              <div className={styles.helpItem}>
                <span>Order and lid</span>
                <p>
                  An order is optional. Stock can be palletised ahead of a sale and assigned later. When you came here from a reservation both source
                  and order are locked to it. The required lid comes from the order line where there is one; a pallet loaded with the wrong lid is a
                  customer rejection, so the mismatch blocks loading.
                </p>
              </div>
              <div className={styles.helpItem}>
                <span>Reserving and palletising are separate</span>
                <p>
                  Earmarking boxes onto a pallet does not reserve them against an order, and a reservation does not put them on a pallet. The same
                  boxes are never consumed twice, and neither action removes them from stock: only a departed load does.
                </p>
              </div>
            </div>
          )}

          {plan && (
            <>
              <div>
                <div className={styles.step}>1 · Source group</div>
                <div className={styles.strong}>{srcLabel(plan.source)}</div>
                <div className={styles.note}>
                  {plan.eligible} boxes eligible · capacity {plan.capacity} per pallet
                  {plan.reservation ? ` · from reservation for ${plan.reservation.order_reference}` : ''}
                </div>
              </div>

              <div>
                <div className={styles.step}>2 · Boxes to palletise</div>
                <input className={styles.qtyInput} inputMode="numeric" aria-label="Boxes to palletise" value={qty} onChange={(e) => setTotal(e.target.value)} />
              </div>

              <div>
                <div className={styles.stepRow}>
                  <div className={styles.step}>3 · Individual pallets · sum of total</div>
                  <button type="button" className={pa.ghostSmall} onClick={() => setParts([...parts, ''])}>
                    Add pallet
                  </button>
                </div>
                <div className={styles.parts}>
                  {parts.map((v, i) => {
                    const n = parseInt(v, 10) || 0
                    return (
                      <div key={i} className={styles.part} data-testid="plan-part">
                        <span className={styles.partLabel}>Pallet {i + 1}</span>
                        <input
                          className={styles.partInput}
                          inputMode="numeric"
                          aria-label={`Pallet ${i + 1} boxes`}
                          value={v}
                          onChange={(e) => setParts(parts.map((x, j) => (j === i ? digits(e.target.value) : x)))}
                        />
                        {n > 0 && n < plan.capacity && <span className={styles.partialChip}>Partial</span>}
                        {parts.length > 1 && (
                          <button
                            type="button"
                            className={styles.removeX}
                            aria-label={`Remove pallet ${i + 1}`}
                            onClick={() => setParts(parts.filter((_, j) => j !== i))}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>

              <div>
                <div className={styles.step}>4 · Order (optional)</div>
                <div className={styles.twoCol}>
                  <div className={styles.full}>
                    {plan.reservation ? (
                      <div className={styles.strong}>
                        {plan.reservation.order_reference}
                        {plan.reservation.customer_name ? ` · ${plan.reservation.customer_name}` : ''}
                      </div>
                    ) : (
                      <select className={styles.input} aria-label="Order" value={orderId} onChange={(e) => chooseOrder(e.target.value)}>
                        <option value="">No order (stock pallet)</option>
                        {choices.map((c) => (
                          <option key={c.order.id} value={c.order.id}>
                            {c.order.reference}
                            {c.order.customer_name ? ` · ${c.order.customer_name}` : ''}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <label>
                    <div className={styles.fieldLabel}>Required lid / brand</div>
                    <select className={styles.input} aria-label="Required lid" value={reqLid} onChange={(e) => setReqLid(e.target.value)}>
                      <option value="">Lid not specified</option>
                      {lidChoices.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <div className={styles.fieldLabel}>Location (optional)</div>
                    <select className={styles.input} aria-label="Location" value={loc} onChange={(e) => setLoc(e.target.value)}>
                      <option value="">Location later</option>
                      {plan.locations.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.name}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className={styles.note} style={{ marginTop: 6 }}>
                  {plan.source.lid
                    ? `Lid at picking: ${plan.source.lid.name}. The required lid here is the request; the applied lid is recorded at confirmation.`
                    : 'No lid recorded at picking.'}
                </div>
              </div>

              {total > 0 && errors.length > 0 && (
                <div className={styles.errList} data-testid="plan-errors">
                  {errors.map((e) => (
                    <div key={e}>{e}</div>
                  ))}
                </div>
              )}
              {block && <div className={styles.amber}>{block}</div>}

              <div className={styles.note}>
                5 · Creating earmarks these boxes so another plan cannot reuse them. Order reservation is a separate dimension. The boxes are not
                consumed twice. A planned pallet does not confirm that a pallet exists.
              </div>
              <div className={sd.actions}>
                <PendingButton className={o.primary} busy={busy} pending="Creating…" disabled={!canCreate} onClick={create}>
                  Create planned pallets
                </PendingButton>
                <button type="button" className={o.ghost} disabled={busy} onClick={requestClose}>
                  Cancel
                </button>
                <Failed text={saveError} />
              </div>
            </>
          )}
        </div>
      </div>
      {confirm}
    </div>
  )
}
